import React, { useState } from 'react';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Loader2 } from 'lucide-react';
import { platformApi } from '@/services/platformApi';
import { useToast } from '@/hooks/use-toast';
import { i18n } from '@/utils/i18n';
import type { InboxTransaction } from '@/utils/inboxFilters';

interface ReleaseEscrowInboxActionProps {
  token: InboxTransaction;
  onReleased?: () => void;
}

function resolveTokenKey(token: InboxTransaction): string | undefined {
  const key = token.key || token.tokenKey || token._id || token.id;
  return key ? String(key) : undefined;
}

function resolveEscrowId(token: InboxTransaction): string | undefined {
  const fromToken = token.escrowId;
  const fromPayload = token.payload && typeof token.payload === 'object' ? (token.payload as { escrowId?: string }).escrowId : undefined;
  const fromOriginator =
    token.originatorPayload && typeof token.originatorPayload === 'object'
      ? (token.originatorPayload as { escrowId?: string }).escrowId
      : undefined;
  return fromToken || fromPayload || fromOriginator;
}

export const ReleaseEscrowInboxAction: React.FC<ReleaseEscrowInboxActionProps> = ({
  token,
  onReleased,
}) => {
  const { toast } = useToast();
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    const tokenKey = resolveTokenKey(token);
    const escrowId = resolveEscrowId(token);
    if (!tokenKey && !escrowId) {
      toast({
        title: i18n.__('inbox.escrow.releaseFailedTitle'),
        description: i18n.__('inbox.escrow.releaseFailedMissingId'),
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await platformApi.tokens.releaseEscrow({
        tokenKey,
        escrowId,
        action: 'release',
      });
      if (result && typeof result === 'object' && result.success === false) {
        throw new Error(
          typeof result.message === 'string' ? result.message : i18n.__('inbox.escrow.releaseFailedGeneric')
        );
      }
      toast({
        title: i18n.__('inbox.escrow.releaseSuccessTitle'),
        description: i18n.__('inbox.escrow.releaseSuccessDesc'),
      });
      setConfirming(false);
      onReleased?.();
    } catch (error) {
      const status = (error as { status?: number })?.status;
      const message =
        error instanceof Error && error.message
          ? error.message
          : i18n.__('inbox.escrow.releaseFailedGeneric');
      toast({
        title: i18n.__('inbox.escrow.releaseFailedTitle'),
        description:
          status === 404
            ? i18n.__('inbox.escrow.releaseFailedMissingApi')
            : message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 pb-3 space-y-3">
      {!confirming ? (
        <Button
          type="button"
          className="w-full"
          onClick={(e) => {
            e.stopPropagation();
            setConfirming(true);
          }}
        >
          {i18n.__('inbox.escrow.releaseButton')}
        </Button>
      ) : (
        <Alert className="bg-white">
          <AlertDescription className="space-y-3 text-sm text-foreground">
            <p className="font-medium">{i18n.__('inbox.escrow.confirmTitle')}</p>
            <ul className="list-disc pl-4 space-y-1.5">
              <li>{i18n.__('inbox.escrow.confirmSatisfied')}</li>
              <li>{i18n.__('inbox.escrow.confirmFundsRelease')}</li>
              <li>{i18n.__('inbox.escrow.confirmRefunds')}</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button
                type="button"
                className="flex-1"
                disabled={submitting}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleConfirm();
                }}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  i18n.__('inbox.escrow.confirmAction')
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={submitting}
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirming(false);
                }}
              >
                {i18n.__('common.cancel')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ReleaseEscrowInboxAction;
